import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import axios from "axios";

export const Contact = () => {
  const { toast } = useToast();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
  });

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: "",
      company: "",
      email: "",
      phone: "",
    };

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    // Company validation
    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
      isValid = false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Phone validation
    const phoneRegex = /^\+?[\d\s-]+$/;
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
      isValid = false;
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const isFormValid = () => {
    return (
      formData.name.trim() !== "" &&
      formData.company.trim() !== "" &&
      formData.email.trim() !== "" &&
      formData.phone.trim() !== "" &&
      Object.values(errors).every(error => error === "") &&
      !loading
    );
  };

  const clearForm = () => {
    setFormData({
      name: "",
      company: "",
      email: "",
      phone: "",
      message: "",
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      toast({
        title: "Form Validation Error",
        description: "Please check the form for errors.",
        variant: "destructive",
      });
      return;
    }

    if (!executeRecaptcha) {
      toast({
        title: "reCAPTCHA Error",
        description: "reCAPTCHA not initialized",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const token = await executeRecaptcha('submit_contact_form');

      const response = await axios.post(
        "https://lab7software.com/api.dynamico/api/Contact/SendContactEmail",
        {
          token,
          email: formData.email,
          name: formData.name,
          phone: formData.phone,
          requirements: formData.message,
        }
      );

      if (response.data.isPassed) {
        toast({
          title: "Success",
          description: "Your message has been successfully sent. We'll get back to you soon!",
        });
        clearForm();
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to send message. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "There was an error sending your message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  return (
    <section id="contact" className="py-20 bg-[#0077bb] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Digital Journey?
          </h2>
          <p className="text-xl text-gray-100">
            Get in touch with our experts today
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 mb-16">
          <div>
            <div className="prose prose-lg text-white max-w-none">
              <h3 className="text-2xl font-bold mb-6 text-white">We'd love to hear from you!</h3>
              <p className="text-gray-100 mb-8">
                For general inquiries or questions to specific departments, please use one of the following options.
              </p>
            </div>
          </div>

          <div className="max-w-lg mx-auto w-full">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg bg-white/10 border ${
                    errors.name ? 'border-red-500' : 'border-white/20'
                  } text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white`}
                  placeholder="Your name"
                />
                {errors.name && (
                  <p className="text-red-300 text-sm mt-1">{errors.name}</p>
                )}
              </div>
              <div>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg bg-white/10 border ${
                    errors.company ? 'border-red-500' : 'border-white/20'
                  } text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white`}
                  placeholder="Company name"
                />
                {errors.company && (
                  <p className="text-red-300 text-sm mt-1">{errors.company}</p>
                )}
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg bg-white/10 border ${
                    errors.email ? 'border-red-500' : 'border-white/20'
                  } text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="text-red-300 text-sm mt-1">{errors.email}</p>
                )}
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg bg-white/10 border ${
                    errors.phone ? 'border-red-500' : 'border-white/20'
                  } text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white`}
                  placeholder="Phone number"
                />
                {errors.phone && (
                  <p className="text-red-300 text-sm mt-1">{errors.phone}</p>
                )}
              </div>
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white"
                  placeholder="How can we help?"
                />
              </div>
              <button
                type="submit"
                disabled={!isFormValid() || loading}
                className="w-full bg-white text-[#0077bb] px-6 py-3 rounded-lg font-semibold hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
